import {
  CancelRounded,
  CheckCircle,
  Close,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  Grid,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import type { Photo } from "../../types/api";

interface OrderPhotoGalleryProps {
  photos: Photo[];
  loading?: boolean;
  note?: string;
}

// Reusable chip component for photo status
interface PhotoStatusChipProps {
  label: string;
  available: boolean;
  variant?: "outlined" | "filled";
}

const PhotoStatusChip: React.FC<PhotoStatusChipProps> = ({
  label,
  available,
  variant = "outlined",
}) => (
  <Chip
    size="small"
    label={label}
    color={available ? "success" : "warning"}
    variant={variant}
    {...(label !== "Preview" && {
      icon: available ? (
        <CheckCircle sx={{ fontSize: 12 }} />
      ) : (
        <CancelRounded sx={{ fontSize: 12 }} />
      ),
    })}
    sx={{ fontSize: "0.65rem", height: 20 }}
  />
);

const OrderPhotoGallery: React.FC<OrderPhotoGalleryProps> = ({
  photos,
  loading = false,
  note,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<
    "preview" | "original" | "final"
  >("preview");

  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
    setSelectedVersion("preview");
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  const getImageUrl = (
    photo: Photo,
    version: "preview" | "original" | "final"
  ) => {
    switch (version) {
      case "preview":
        return photo.previewUrl;
      case "original":
        return photo.originalUrl;
      case "final":
        return photo.finalUrl || "";
      default:
        return photo.previewUrl;
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <ImageIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Order Photos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {photos.length} photo{photos.length !== 1 ? "s" : ""} in this
              order
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            ))}
          </Grid>
        ) : photos.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "grey.50",
              borderRadius: 2,
            }}
          >
            <ImageIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No photos in this order
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {photos.map((photo, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={photo.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={() => handlePhotoClick(photo)}
                >
                  <Box
                    sx={{
                      position: "relative",
                      paddingTop: "100%",
                      bgcolor: "grey.100",
                      overflow: "hidden",
                    }}
                  >
                    {/* Index Badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 1,
                        bgcolor: "rgba(0, 0, 0, 0.75)",
                        color: "white",
                        borderRadius: "4px",
                        px: 1,
                        py: 0.5,
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      #{index + 1}
                    </Box>

                    <Box
                      component="img"
                      src={photo.previewUrl}
                      alt={`Photo ${photo.id}`}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        size="small"
                        label="Preview"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem", height: 20 }}
                      />
                      <PhotoStatusChip
                        label="Original"
                        available={!!photo.originalUrl}
                      />
                      <PhotoStatusChip
                        label="Final"
                        available={!!photo.finalUrl}
                      />
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Order Notes Section */}
        {note && (
          <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Order Notes
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {note}
            </Typography>
          </Box>
        )}

        {/* Photo Viewer Dialog */}
        <Dialog open={!!selectedPhoto} onClose={handleClose} maxWidth="lg">
          {selectedPhoto && (
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "white",
                  bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                  zIndex: 1,
                }}
              >
                <Close />
              </IconButton>

              {/* Version Tabs */}
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  display: "flex",
                  gap: 1,
                  zIndex: 1,
                }}
              >
                <Chip
                  label="Preview"
                  onClick={() => setSelectedVersion("preview")}
                  color={selectedVersion === "preview" ? "primary" : "default"}
                  sx={{ cursor: "pointer" }}
                />
                <Chip
                  label="Original"
                  onClick={() => setSelectedVersion("original")}
                  color={selectedVersion === "original" ? "primary" : "default"}
                  disabled={!selectedPhoto.originalUrl}
                  sx={{
                    cursor: selectedPhoto.originalUrl ? "pointer" : "default",
                  }}
                />
                <Chip
                  label="Final"
                  onClick={() => setSelectedVersion("final")}
                  color={selectedVersion === "final" ? "primary" : "default"}
                  disabled={!selectedPhoto.finalUrl}
                  sx={{
                    cursor: selectedPhoto.finalUrl ? "pointer" : "default",
                  }}
                />
              </Box>

              {/* Image Display */}
              <Box
                component="img"
                src={getImageUrl(selectedPhoto, selectedVersion)}
                alt={`Photo ${selectedPhoto.id} - ${selectedVersion}`}
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          )}
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default OrderPhotoGallery;
